import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TermPeriodsCard from '../components/TermPeriodsCard';
import PeriodTimesCard from '../components/PeriodTimesCard';

export default function AcademicDashboard() {
  const [searchParams] = useSearchParams();
  const [tab, setTab]   = useState(searchParams.get('tab') === 'terms' ? 'terms' : 'overview');
  const [overview, setOverview] = useState(null);
  const [atRisk, setAtRisk]     = useState([]);
  const [thresholds, setT]      = useState({});
  const [allClasses, setAllCls] = useState([]);
  const [logs, setLogs]         = useState([]);
  const [err, setErr]           = useState('');
  const [globalT, setGlobalT]   = useState(75);

  const auth = () => ({
    Authorization: 'Bearer ' + localStorage.getItem('token'),
    'Content-Type': 'application/json'
  });

  const loadOverview = async () => {
    try {
      const r = await fetch('/api/academic/dashboard', { headers: auth() });
      const d = await r.json();
      setOverview(d.dashboard || d);
    } catch (e) { setErr(e.message); }
  };
  const loadAtRisk = async () => {
    try {
      const r = await fetch('/api/academic/at-risk', { headers: auth() });
      const d = await r.json();
      setAtRisk(d.students || d.data || []);
    } catch (e) { setErr(e.message); }
  };
  const loadThresholds = async () => {
    try {
      const r = await fetch('/api/academic/alert-thresholds', { headers: auth() });
      const d = await r.json();
      setT(d);
      if (d.global_threshold || d.threshold) setGlobalT(d.global_threshold || d.threshold);
    } catch (e) { setErr(e.message); }
  };
  const loadAllClasses = async () => {
    try {
      const r = await fetch('/api/academic/classes', { headers: auth() });
      const d = await r.json();
      setAllCls(d.classes || d.data || []);
    } catch (e) { setErr(e.message); }
  };
  const loadLogs = async () => {
    try {
      const r = await fetch('/api/academic/alert-logs', { headers: auth() });
      const d = await r.json();
      setLogs(d.logs || d.data || []);
    } catch (e) { setErr(e.message); }
  };

  useEffect(() => {
    if (tab === 'overview')   loadOverview();
    if (tab === 'at-risk')    loadAtRisk();
    if (tab === 'thresholds') loadThresholds();
    if (tab === 'classes')    loadAllClasses();
    if (tab === 'logs')       loadLogs();
    // eslint-disable-next-line
  }, [tab]);

  const saveGlobalThreshold = async () => {
    try {
      const r = await fetch('/api/academic/alert-thresholds', {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({ threshold_percent: parseInt(globalT) })
      });
      if (!r.ok) throw new Error('保存失敗');
      loadThresholds();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>教務ダッシュボード</h1>
        <p>全学の出席状況を一括管理します。</p>
      </div>

      <div className="tab-bar">
        <button className={'tab-btn'+(tab==='overview'?' active':'')}   onClick={()=>setTab('overview')}>概要</button>
        <button className={'tab-btn'+(tab==='at-risk'?' active':'')}    onClick={()=>setTab('at-risk')}>リスク学生</button>
        <button className={'tab-btn'+(tab==='classes'?' active':'')}    onClick={()=>setTab('classes')}>全授業</button>
        <button className={'tab-btn'+(tab==='thresholds'?' active':'')} onClick={()=>setTab('thresholds')}>しきい値</button>
        <button className={'tab-btn'+(tab==='logs'?' active':'')}       onClick={()=>setTab('logs')}>アラートログ</button>
        <button className={'tab-btn'+(tab==='terms'?' active':'')}      onClick={()=>setTab('terms')}>学期の期間</button>
      </div>

      {err && <div className="card" style={{borderLeft:'4px solid #ef4444'}}><p style={{color:'#c62828'}}>{err}</p></div>}

      {/* 概要 */}
      {tab === 'overview' && overview && (
        <div className="stats-grid">
          <Stat label="総学生数"     value={overview.total_students || 0}     color="#3b82f6"/>
          <Stat label="総授業数"     value={overview.total_classes  || 0}     color="#8b5cf6"/>
          <Stat label="リスク学生"   value={overview.at_risk_students || 0}   color="#ef4444"/>
          <Stat label="平均出席率"   value={(overview.average_attendance_rate ?? 0) + '%'} color="#22c55e"/>
        </div>
      )}

      {/* リスク学生 */}
      {tab === 'at-risk' && (
        <div className="card">
          <div className="card-header"><h2>リスク学生 ({atRisk.length})</h2></div>
          {atRisk.length === 0 ? <p style={{color:'#888'}}>リスク学生はいません</p> : (
            <table className="data-table">
              <thead><tr><th>学籍番号</th><th>氏名</th><th>授業</th><th>出席率</th><th>しきい値</th></tr></thead>
              <tbody>{atRisk.map((s,i) => (
                <tr key={i}>
                  <td>{s.student_id}</td>
                  <td><strong>{s.name}</strong></td>
                  <td>{s.class_name || '横断'}</td>
                  <td><strong style={{color:'#ef4444'}}>{s.attendance_rate}%</strong></td>
                  <td>{s.threshold || globalT}%</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* 全授業 */}
      {tab === 'classes' && (
        <div className="card">
          <div className="card-header"><h2>全授業（横断ビュー）</h2></div>
          {allClasses.length === 0 ? <p style={{color:'#888'}}>授業がありません</p> : (
            <table className="data-table">
              <thead><tr><th>授業名</th><th>担当</th><th>履修者</th><th>平均出席率</th><th>リスク学生</th></tr></thead>
              <tbody>{allClasses.map(c => (
                <tr key={c.id}>
                  <td><strong>{c.name}</strong></td>
                  <td>{c.teacher_name || '-'}</td>
                  <td>{c.total_students || 0}</td>
                  <td>{c.average_attendance_rate ?? '-'}{c.average_attendance_rate != null ? '%' : ''}</td>
                  <td>{c.at_risk_count || 0}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {/* しきい値 */}
      {tab === 'thresholds' && (
        <div className="card">
          <div className="card-header"><h2>アラートしきい値</h2></div>
          <div className="form-row">
            <label>グローバルしきい値 (%)</label>
            <div style={{display:'flex',gap:'0.5rem'}}>
              <input type="number" value={globalT} onChange={e=>setGlobalT(e.target.value)} style={{width:100}}/>
              <button className="btn-primary" onClick={saveGlobalThreshold}>保存</button>
            </div>
            <small style={{color:'#888'}}>この値を下回る学生がリスクとして検出されます</small>
          </div>
          <pre style={{background:'#f9fafb',padding:'1rem',borderRadius:'6px',marginTop:'1rem',fontSize:'0.85rem',overflow:'auto'}}>
{JSON.stringify(thresholds, null, 2)}
          </pre>
        </div>
      )}

      {/* 学期の期間【2026-09-17】授業を選ぶ欄の「今の期間」はここから決まる */}
      {tab === 'terms' && <><TermPeriodsCard /><PeriodTimesCard /></>}

      {/* ログ */}
      {tab === 'logs' && (
        <div className="card">
          <div className="card-header"><h2>アラートログ ({logs.length})</h2></div>
          {logs.length === 0 ? <p style={{color:'#888'}}>ログがありません</p> : (
            <table className="data-table">
              <thead><tr><th>日時</th><th>学生</th><th>授業</th><th>出席率</th><th>通知済</th></tr></thead>
              <tbody>{logs.map((l,i) => (
                <tr key={i}>
                  <td>{l.created_at ? new Date(l.created_at).toLocaleString('ja-JP') : '-'}</td>
                  <td>{l.student_name} ({l.student_id})</td>
                  <td>{l.class_name || '-'}</td>
                  <td>{l.attendance_rate}%</td>
                  <td>{l.notified ? '通知済' : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="stat-card" style={{ borderTop: '4px solid ' + color }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
    </div>
  );
}
